import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { getIdToken } from '../lib/auth';
import { Avatar } from '../components/Avatar';
import BackButton from '../components/BackButton';
import { TimeEntryWidget } from '../components/TimeEntryWidget';
import { TaskModal } from '../components/TaskModal';
import { EventModal } from '../components/EventModal';
import { CreateInvoiceModal } from '../components/CreateInvoiceModal';
import { UploadDocumentDialog } from '../components/UploadDocumentDialog';
import { SendDocumentModal } from '../components/SendDocumentModal';
import { CaseContactModal } from '../components/CaseContactModal';
import { CaseEditModal } from '../components/CaseEditModal';
import { CaseExpenseModal } from '../components/CaseExpenseModal';
import { CaseTimelineEventModal } from '../components/CaseTimelineEventModal';
import { InvoiceReminderModal } from '../components/InvoiceReminderModal';
import { MessageThread } from '../components/MessageThread';
import { ExtractedMarkdown } from '../components/ExtractedMarkdown';
import { CaseProceduralPane } from './CaseProceduralPane';
import type {
  CalendarEventSummary,
  CaseContactSummary,
  CaseExpenseSummary,
  CaseNoteSummary,
  CaseSummary,
  CaseTimelineEventSummary,
  ClientSummary,
  DocumentSummary,
  InvoiceStatus,
  InvoiceSummary,
  TaskStatusKey,
  TaskSummary,
  TimeEntrySummary,
} from '../types';

type CaseFull = CaseSummary;
type ListResp<T> = { items: T[] };
type ExpensesListResp = ListResp<CaseExpenseSummary> & {
  summary: { totalAgorot: number; paidAgorot: number; pendingAgorot: number; count: number };
};

const TABS = [
  'overview',
  'documents',
  'contacts',
  'tasks',
  'time',
  'expenses',
  'procedural',
  'timeline',
  'invoices',
  'messages',
  'notes',
] as const;
type TabKey = (typeof TABS)[number];

function formatIls(agorot: number): string {
  const shekels = agorot / 100;
  if (shekels >= 1000) return `₪${(shekels / 1000).toFixed(1)}K`;
  return `₪${shekels.toFixed(0)}`;
}

function formatIlsExact(agorot: number): string {
  return `₪${(agorot / 100).toLocaleString('he-IL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

export function CaseDetail({ firmId }: { firmId: string }) {
  const { t, i18n } = useTranslation(['cases', 'common', 'tasks']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [c, setC] = useState<CaseFull | null>(null);
  const [docs, setDocs] = useState<DocumentSummary[]>([]);
  const [tasks, setTasks] = useState<TaskSummary[]>([]);
  const [time, setTime] = useState<TimeEntrySummary[]>([]);
  const [events, setEvents] = useState<CalendarEventSummary[]>([]);
  const [invoices, setInvoices] = useState<InvoiceSummary[]>([]);
  const [contacts, setContacts] = useState<CaseContactSummary[]>([]);
  const [expenses, setExpenses] = useState<CaseExpenseSummary[]>([]);
  const [expenseSummary, setExpenseSummary] = useState<ExpensesListResp['summary']>({
    totalAgorot: 0,
    paidAgorot: 0,
    pendingAgorot: 0,
    count: 0,
  });
  const [timeline, setTimeline] = useState<CaseTimelineEventSummary[]>([]);
  const [notes, setNotes] = useState<CaseNoteSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modals
  const [taskModal, setTaskModal] = useState<{ task?: TaskSummary } | null>(null);
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [sendDoc, setSendDoc] = useState<DocumentSummary | null>(null);
  const [contactModal, setContactModal] = useState<{ contact?: CaseContactSummary } | null>(null);
  const [expenseModal, setExpenseModal] = useState<{ expense?: CaseExpenseSummary } | null>(null);
  const [timelineModal, setTimelineModal] = useState<{
    event?: CaseTimelineEventSummary;
  } | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  // Channel preselect for the document-send modal — set when the user
  // clicks a per-row WA or Mail shortcut. `null` = generic "Send" button
  // (modal picks the default channel).
  const [sendDocChannel, setSendDocChannel] = useState<'wa' | 'email' | null>(null);
  // Invoice reminder modal — opened from per-row "תזכורת" button on
  // outstanding invoices.
  const [reminderInvoice, setReminderInvoice] = useState<InvoiceSummary | null>(null);
  // Bulk send-all-documents modal trigger.
  const [bulkSendOpen, setBulkSendOpen] = useState(false);

  // For UploadDocumentDialog → /api/documents POST flow
  const [clients, setClients] = useState<ClientSummary[]>([]);

  const tabRaw = searchParams.get('tab') ?? 'overview';
  const activeTab: TabKey = (TABS as readonly string[]).includes(tabRaw)
    ? (tabRaw as TabKey)
    : 'overview';

  function setTab(tab: TabKey) {
    const next = new URLSearchParams(searchParams);
    if (tab === 'overview') next.delete('tab');
    else next.set('tab', tab);
    setSearchParams(next, { replace: true });
  }

  const loadAll = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [
        caseData,
        d,
        ta,
        ti,
        ev,
        inv,
        co,
        ex,
        tl,
        no,
      ] = await Promise.all([
        apiFetch<CaseFull>(`/api/cases/${id}`),
        apiFetch<ListResp<DocumentSummary>>(`/api/documents?caseId=${id}`),
        apiFetch<ListResp<TaskSummary>>(`/api/tasks?caseId=${id}`),
        apiFetch<ListResp<TimeEntrySummary>>(`/api/time-entries?caseId=${id}`),
        apiFetch<ListResp<CalendarEventSummary>>(`/api/calendar/events?caseId=${id}`),
        apiFetch<ListResp<InvoiceSummary>>(`/api/invoices?caseId=${id}`),
        apiFetch<ListResp<CaseContactSummary>>(`/api/cases/${id}/contacts`),
        apiFetch<ExpensesListResp>(`/api/cases/${id}/expenses`),
        apiFetch<ListResp<CaseTimelineEventSummary>>(`/api/cases/${id}/timeline`),
        apiFetch<ListResp<CaseNoteSummary>>(`/api/cases/${id}/notes`),
      ]);
      setC(caseData);
      setDocs(d.items);
      setTasks(ta.items);
      setTime(ti.items);
      setEvents(ev.items);
      setInvoices(inv.items);
      setContacts(co.items);
      setExpenses(ex.items);
      setExpenseSummary(ex.summary);
      setTimeline(tl.items);
      setNotes(no.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  // Load clients lazily — only used when the upload dialog opens.
  useEffect(() => {
    if (!uploadOpen || clients.length > 0) return;
    apiFetch<ListResp<ClientSummary>>('/api/clients')
      .then((r) => setClients(r.items))
      .catch(() => setClients([]));
  }, [uploadOpen, clients.length]);

  const lang = i18n.language;
  const fmtDate = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString(lang, { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

  if (loading && !c) {
    return (
      <div className="full-panel">
        <div className="empty">{t('detail.loading')}</div>
      </div>
    );
  }
  if (error || !c) {
    return (
      <div className="full-panel">
        <div className="empty">{error ?? t('detail.not_found')}</div>
      </div>
    );
  }

  const clientName = c.client?.name ?? '—';
  const totalMinutes = time.reduce((s, e) => (e.billable ? s + e.minutes : s), 0);
  const totalHours = Math.round((totalMinutes / 60) * 10) / 10;
  const totalFeeAgorot = time.reduce((s, e) => {
    if (!e.billable) return s;
    const rate = e.hourlyRateIls ?? c.hourlyRateIls ?? 0;
    return s + Math.round((e.minutes / 60) * rate * 100);
  }, 0);

  const isUrgent = c.priority === 'urgent' || c.priority === 'high';

  return (
    <div className="case-detail has-bottom-nav">
      {/* Hero */}
      <div className="case-hero">
        <Avatar name={clientName} size="lg" />
        <div className="case-hero-body">
          <div className="case-hero-title" dir="auto">
            {clientName}
            {c.area ? ` — ${c.area}` : ''}
            {isUrgent && (
              <span className="tb-urgent-pill">{t('detail.label_urgent')}</span>
            )}
          </div>
          <div className="case-hero-sub" dir="auto">
            {t('detail.hero_sub', {
              caseNumber: c.caseNumber,
              openedAt: fmtDate(c.openedAt),
            })}
          </div>
          <div className="case-hero-pills">
            <span className={`pill pill-status-${c.status}`}>{t(`statuses.${c.status}`)}</span>
            <span className={`pill pill-prio-${c.priority}`}>
              {t(`priorities.${c.priority}`)}
            </span>
            {c.area && <span className="pill pill-area" dir="auto">{c.area}</span>}
          </div>
        </div>
        <div className="case-hero-stats">
          <div className="hstat">
            <div className="hstat-val">{totalHours}</div>
            <div className="hstat-label">{t('detail.hero_stat_hours')}</div>
          </div>
          <div className="hstat">
            <div className="hstat-val">{formatIls(totalFeeAgorot)}</div>
            <div className="hstat-label">{t('detail.hero_stat_fee')}</div>
          </div>
          <div className="hstat">
            <div className="hstat-val">{formatIls(expenseSummary.totalAgorot)}</div>
            <div className="hstat-label">{t('detail.hero_stat_expenses')}</div>
          </div>
        </div>
        <div className="case-hero-actions">
          <button className="btn-primary" onClick={() => setEventModalOpen(true)}>
            + {t('detail.action_event')}
          </button>
          <button className="btn-cancel" onClick={() => setTab('messages')}>
            {t('detail.action_update_client')}
          </button>
          <button className="btn-cancel" onClick={() => window.print()}>
            {t('detail.action_print')}
          </button>
          <button className="btn-cancel" onClick={() => setEditOpen(true)}>
            {t('detail.action_edit')}
          </button>
          <BackButton className="btn-cancel" to="/cases" label={t('detail.back')} />
        </div>
      </div>

      {/* Tabs */}
      <div className="case-tabs">
        {TABS.map((tab) => (
          <button
            key={tab}
            className={`case-tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setTab(tab)}
          >
            {t(`detail.tabs.${tab}`)}
            {tabCount(tab, { docs, contacts, tasks, invoices, notes }) != null && (
              <span className="case-tab-count">
                {tabCount(tab, { docs, contacts, tasks, invoices, notes })}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Panes */}
      <div className="case-pane">
        {activeTab === 'overview' && (
          <OverviewPane
            c={c}
            time={time}
            invoices={invoices}
            expenses={expenses}
            onUpdated={() => void loadAll()}
          />
        )}
        {activeTab === 'documents' && (
          <DocumentsPane
            docs={docs}
            onUpload={() => setUploadOpen(true)}
            onSend={(doc, channel) => {
              setSendDocChannel(channel ?? null);
              setSendDoc(doc);
            }}
            onSendAll={() => setBulkSendOpen(true)}
            onOpenDoc={() => navigate('/documents')}
            fmtDate={fmtDate}
          />
        )}
        {activeTab === 'contacts' && (
          <ContactsPane
            c={c}
            contacts={contacts}
            onAdd={() => setContactModal({})}
            onEdit={(contact) => setContactModal({ contact })}
          />
        )}
        {activeTab === 'tasks' && (
          <TasksPane
            tasks={tasks}
            onAdd={() => setTaskModal({})}
            onEdit={(task) => setTaskModal({ task })}
            onToggle={async (task) => {
              const newStatus: TaskStatusKey = task.status === 'done' ? 'open' : 'done';
              setTasks((prev) =>
                prev.map((tt) =>
                  tt.id === task.id
                    ? {
                        ...tt,
                        status: newStatus,
                        completedAt: newStatus === 'done' ? new Date().toISOString() : null,
                      }
                    : tt,
                ),
              );
              try {
                await apiFetch(`/api/tasks/${task.id}`, {
                  method: 'PATCH',
                  body: JSON.stringify({ status: newStatus }),
                });
              } catch {
                setTasks((prev) => prev.map((tt) => (tt.id === task.id ? task : tt)));
              }
            }}
          />
        )}
        {activeTab === 'time' && (
          <TimePane
            c={c}
            time={time}
            invoices={invoices}
            onSaved={() => void loadAll()}
            fmtDate={fmtDate}
          />
        )}
        {activeTab === 'expenses' && (
          <ExpensesPane
            expenses={expenses}
            summary={expenseSummary}
            onAdd={() => setExpenseModal({})}
            onEdit={(expense) => setExpenseModal({ expense })}
            fmtDate={fmtDate}
          />
        )}
        {activeTab === 'procedural' && (
          <CaseProceduralPane
            caseId={c.id}
            initialTrack={c.proceduralTrack}
            initialPhase={c.proceduralPhase}
            onUpdated={() => void loadAll()}
          />
        )}
        {activeTab === 'timeline' && (
          <TimelinePane
            timeline={timeline}
            onAdd={() => setTimelineModal({})}
            onEdit={(event) => setTimelineModal({ event })}
            fmtDate={fmtDate}
          />
        )}
        {activeTab === 'invoices' && (
          <InvoicesPane
            invoices={invoices}
            onAdd={() => setInvoiceModalOpen(true)}
            onSendReminder={(inv) => setReminderInvoice(inv)}
            fmtDate={fmtDate}
          />
        )}
        {activeTab === 'messages' && c.client && (
          <MessagesPane c={c} firmId={firmId} />
        )}
        {activeTab === 'notes' && (
          <NotesPane
            caseId={c.id}
            caseFields={{
              description: c.description,
              procedureType: c.procedureType,
              court: c.court,
              area: c.area,
            }}
            notes={notes}
            onChanged={() => void loadAll()}
          />
        )}
      </div>

      {/* Modals */}
      {taskModal && (
        <TaskModal
          {...(taskModal.task ? { initial: taskModal.task } : {})}
          onSaved={() => {
            setTaskModal(null);
            void loadAll();
          }}
          onClose={() => setTaskModal(null)}
          onDeleted={() => {
            setTaskModal(null);
            void loadAll();
          }}
        />
      )}
      {eventModalOpen && (
        <EventModal
          onSaved={() => {
            setEventModalOpen(false);
            void loadAll();
          }}
          onClose={() => setEventModalOpen(false)}
        />
      )}
      {invoiceModalOpen && (
        <CreateInvoiceModal
          clients={c.client ? [{ id: c.client.id, name: c.client.name } as ClientSummary] : []}
          onCreated={() => {
            setInvoiceModalOpen(false);
            void loadAll();
          }}
          onClose={() => setInvoiceModalOpen(false)}
        />
      )}
      {uploadOpen && (
        <UploadDocumentDialog
          onConfirm={async ({ clientId, caseId }) => {
            setUploadOpen(false);
            // Trigger native file picker — UploadDocumentDialog only collects
            // the assignment; the actual upload is handled on /documents.
            // Simpler: route to /documents which has the upload flow wired.
            navigate(`/documents?clientId=${clientId}&caseId=${caseId ?? ''}`);
          }}
          onClose={() => setUploadOpen(false)}
        />
      )}
      {sendDoc && (
        <SendDocumentModal
          document={sendDoc}
          firmId={firmId}
          {...(sendDocChannel ? { initialChannel: sendDocChannel } : {})}
          onSent={() => {
            setSendDoc(null);
            setSendDocChannel(null);
          }}
          onClose={() => {
            setSendDoc(null);
            setSendDocChannel(null);
          }}
        />
      )}
      {bulkSendOpen && docs.length > 0 && (
        <SendDocumentModal
          documents={docs}
          firmId={firmId}
          onSent={() => setBulkSendOpen(false)}
          onClose={() => setBulkSendOpen(false)}
        />
      )}
      {contactModal && id && (
        <CaseContactModal
          caseId={id}
          {...(contactModal.contact ? { initial: contactModal.contact } : {})}
          onSaved={() => {
            setContactModal(null);
            void loadAll();
          }}
          onClose={() => setContactModal(null)}
          onDeleted={() => {
            setContactModal(null);
            void loadAll();
          }}
        />
      )}
      {expenseModal && id && (
        <CaseExpenseModal
          caseId={id}
          {...(expenseModal.expense ? { initial: expenseModal.expense } : {})}
          onSaved={() => {
            setExpenseModal(null);
            void loadAll();
          }}
          onClose={() => setExpenseModal(null)}
          onDeleted={() => {
            setExpenseModal(null);
            void loadAll();
          }}
        />
      )}
      {timelineModal && id && (
        <CaseTimelineEventModal
          caseId={id}
          {...(timelineModal.event ? { initial: timelineModal.event } : {})}
          onSaved={() => {
            setTimelineModal(null);
            void loadAll();
          }}
          onClose={() => setTimelineModal(null)}
          onDeleted={() => {
            setTimelineModal(null);
            void loadAll();
          }}
        />
      )}
      {editOpen && (
        <CaseEditModal
          initial={c}
          onSaved={() => {
            setEditOpen(false);
            void loadAll();
          }}
          onClose={() => setEditOpen(false)}
        />
      )}
      {reminderInvoice && (
        <InvoiceReminderModal
          invoice={reminderInvoice}
          clientId={c.client?.id ?? null}
          firmId={firmId}
          onSent={() => setReminderInvoice(null)}
          onClose={() => setReminderInvoice(null)}
        />
      )}

      {/* Mobile bottom-nav — primary 5 tabs (mockup parity) */}
      <nav className="case-bottom-nav">
        {(['overview', 'documents', 'tasks', 'procedural', 'invoices'] as const).map((tab) => (
          <button
            key={tab}
            className={`case-bnav ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setTab(tab)}
            type="button"
          >
            <BottomNavIcon tab={tab} />
            {t(`detail.bottom_nav.${tab}`)}
          </button>
        ))}
      </nav>
    </div>
  );
}

function BottomNavIcon({ tab }: { tab: 'overview' | 'documents' | 'tasks' | 'procedural' | 'invoices' }) {
  switch (tab) {
    case 'overview':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="3" />
          <path d="M2 12h2M20 12h2M12 2v2M12 20v2" />
        </svg>
      );
    case 'documents':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
          <polyline points="14 2 14 8 20 8" />
        </svg>
      );
    case 'tasks':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M9 11l3 3L22 4" />
          <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
        </svg>
      );
    case 'procedural':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <line x1="8" y1="6" x2="21" y2="6" />
          <line x1="8" y1="12" x2="21" y2="12" />
          <line x1="8" y1="18" x2="21" y2="18" />
          <line x1="3" y1="6" x2="3.01" y2="6" />
          <line x1="3" y1="12" x2="3.01" y2="12" />
          <line x1="3" y1="18" x2="3.01" y2="18" />
        </svg>
      );
    case 'invoices':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect x="2" y="5" width="20" height="14" rx="2" />
          <path d="M2 10h20" />
        </svg>
      );
  }
}

function tabCount(
  tab: TabKey,
  data: {
    docs: DocumentSummary[];
    contacts: CaseContactSummary[];
    tasks: TaskSummary[];
    invoices: InvoiceSummary[];
    notes: CaseNoteSummary[];
  },
): number | null {
  switch (tab) {
    case 'documents':
      return data.docs.length;
    case 'contacts':
      // includes the primary client
      return data.contacts.length + 1;
    case 'tasks':
      return data.tasks.length;
    case 'invoices':
      return data.invoices.length;
    case 'notes':
      return data.notes.length;
    default:
      return null;
  }
}

// ─── Notes ───────────────────────────────────────────────────────────

const COPY_FIELDS = ['description', 'procedureType', 'court', 'area'] as const;
type CopyField = (typeof COPY_FIELDS)[number];

function NotesPane({
  caseId,
  caseFields,
  notes,
  onChanged,
}: {
  caseId: string;
  caseFields: Record<CopyField, string | null>;
  notes: CaseNoteSummary[];
  onChanged: () => void;
}) {
  const { t, i18n } = useTranslation(['cases', 'common']);
  const [body, setBody] = useState('');
  const [isPrivate, setIsPrivate] = useState(true);
  const [autoProcess, setAutoProcess] = useState(true);
  const [pickedFile, setPickedFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Local mirror of notes so SSE updates can mutate state without
  // round-tripping the full list. Reset whenever the parent's prop
  // changes (initial load or explicit refetch).
  const [localNotes, setLocalNotes] = useState(notes);
  useEffect(() => setLocalNotes(notes), [notes]);

  // Live updates via SSE — fetch+ReadableStream so we can pass auth.
  const streamAbortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    const ctrl = new AbortController();
    streamAbortRef.current = ctrl;
    (async () => {
      try {
        const idToken = (await getIdToken()) ?? '';
        const res = await fetch(`/api/cases/${caseId}/notes/stream`, {
          headers: { Authorization: `Bearer ${idToken}` },
          signal: ctrl.signal,
        });
        if (!res.ok || !res.body) return;
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const lines = buf.split('\n');
          buf = lines.pop() ?? '';
          for (const line of lines) {
            const m = /^data: (.+)$/.exec(line);
            if (!m) continue;
            let evt: { type: string; kind?: string; note?: Partial<CaseNoteSummary> & { id: string } };
            try { evt = JSON.parse(m[1]!); } catch { continue; }
            if (evt.type !== 'note' || !evt.note) continue;
            const incoming = evt.note;
            setLocalNotes((prev) => {
              const idx = prev.findIndex((p) => p.id === incoming.id);
              if (idx === -1) {
                if (evt.kind === 'updated') return prev;
                // Created via SSE — fall back to refetching so we get
                // author etc. (the stream payload is partial).
                onChanged();
                return prev;
              }
              const next = prev.slice();
              next[idx] = { ...next[idx], ...incoming } as CaseNoteSummary;
              return next;
            });
          }
        }
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          // eslint-disable-next-line no-console
          console.warn('[case-notes-stream] disconnected', err);
        }
      }
    })();
    return () => {
      ctrl.abort();
      streamAbortRef.current = null;
    };
  }, [caseId, onChanged]);

  async function uploadFile(file: File): Promise<string> {
    const presign = await apiFetch<{ documentId: string; uploadUrl: string; key: string }>(
      '/api/documents/upload-url',
      {
        method: 'POST',
        body: JSON.stringify({ name: file.name, mime: file.type, size: file.size, caseId }),
      },
    );
    const put = await fetch(presign.uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type },
    });
    if (!put.ok) throw new Error(`upload_failed:${put.status}`);
    await apiFetch('/api/documents', {
      method: 'POST',
      body: JSON.stringify({
        id: presign.documentId,
        name: file.name,
        mime: file.type,
        size: file.size,
        r2Key: presign.key,
        caseId,
      }),
    });
    return presign.documentId;
  }

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!body.trim() && !pickedFile) return;
    setBusy(true);
    setError(null);
    try {
      let documentId: string | null = null;
      if (pickedFile) {
        documentId = await uploadFile(pickedFile);
      }
      await apiFetch(`/api/cases/${caseId}/notes`, {
        method: 'POST',
        body: JSON.stringify({
          body: body.trim() || (pickedFile ? `📎 ${pickedFile.name}` : ''),
          isPrivate,
          ...(documentId ? { documentId, autoProcess } : {}),
        }),
      });
      setBody('');
      setPickedFile(null);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(noteId: string) {
    if (!window.confirm(t('detail.notes_delete_confirm'))) return;
    try {
      await apiFetch(`/api/cases/${caseId}/notes/${noteId}`, { method: 'DELETE' });
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function togglePrivacy(note: CaseNoteSummary) {
    try {
      await apiFetch(`/api/cases/${caseId}/notes/${note.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ isPrivate: !note.isPrivate }),
      });
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function handleRetry(noteId: string) {
    try {
      await apiFetch(`/api/cases/${caseId}/notes/${noteId}/retry`, { method: 'POST' });
      // SSE will push the status update.
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  // When the user picks a target field, we either open a confirm
  // dialog (if the field already has content — they need to choose
  // append vs overwrite knowingly) or fast-path the append (empty
  // field, no ambiguity).
  const [copyTarget, setCopyTarget] = useState<{ note: CaseNoteSummary; field: CopyField } | null>(null);

  async function performCopy(noteId: string, field: CopyField, mode: 'append' | 'overwrite') {
    try {
      await apiFetch(`/api/cases/${caseId}/notes/${noteId}/copy-to-field`, {
        method: 'POST',
        body: JSON.stringify({ field, mode }),
      });
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  function handlePickCopyField(note: CaseNoteSummary, field: CopyField) {
    const existing = caseFields[field]?.trim();
    if (!existing) {
      // Empty target: nothing to lose. Append (the only sensible mode
      // for an empty field) without bothering the user.
      void performCopy(note.id, field, 'append');
      return;
    }
    setCopyTarget({ note, field });
  }

  async function openAttachment(documentId: string) {
    try {
      const res = await apiFetch<{ url: string }>(
        `/api/documents/${documentId}/download-url`,
      );
      window.open(res.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  const fmtDateTime = (iso: string) =>
    new Date(iso).toLocaleString(i18n.language, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  return (
    <div className="full-panel">
      <div className="panel-header">{t('detail.section_notes')}</div>

      <form onSubmit={handleAdd} className="form-section" style={{ paddingBottom: 0 }}>
        <div className="field">
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={t('detail.notes_placeholder')}
            rows={3}
          />
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginTop: 8,
            flexWrap: 'wrap',
          }}
        >
          <label
            className="btn-cancel"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              cursor: 'pointer',
              padding: '6px 12px',
              fontSize: 12,
            }}
          >
            📎 {pickedFile ? pickedFile.name : t('detail.notes_attach_file')}
            <input
              type="file"
              accept="application/pdf,image/*,audio/*"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null;
                setPickedFile(f);
              }}
            />
          </label>
          {pickedFile && (
            <>
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setPickedFile(null)}
                style={{ padding: '4px 10px', fontSize: 11 }}
              >
                ×
              </button>
              <label
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}
              >
                <input
                  type="checkbox"
                  checked={autoProcess}
                  onChange={(e) => setAutoProcess(e.target.checked)}
                />
                {t('detail.notes_auto_process')}
              </label>
            </>
          )}
          <label
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
          >
            <input
              type="checkbox"
              checked={isPrivate}
              onChange={(e) => setIsPrivate(e.target.checked)}
            />
            <span style={{ fontSize: 13 }}>
              {isPrivate ? '🔒 ' : '👥 '}
              {t(isPrivate ? 'detail.notes_private' : 'detail.notes_firm')}
            </span>
          </label>
          <button
            type="submit"
            className="btn-save"
            disabled={busy || (!body.trim() && !pickedFile)}
            style={{ marginInlineStart: 'auto' }}
          >
            {busy ? t('common:actions.loading_dots') : t('detail.notes_add')}
          </button>
        </div>
        {error && <div className="signin-error" style={{ marginTop: 8 }}>{error}</div>}
      </form>

      {localNotes.length === 0 ? (
        <div className="empty">{t('detail.empty_notes')}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 16 }}>
          {localNotes.map((n) => (
            <NoteRow
              key={n.id}
              note={n}
              fmtDateTime={fmtDateTime}
              onTogglePrivacy={() => togglePrivacy(n)}
              onDelete={() => handleDelete(n.id)}
              onRetry={() => handleRetry(n.id)}
              onCopyToField={(field) => handlePickCopyField(n, field)}
              onOpenAttachment={() => n.documentId && openAttachment(n.documentId)}
            />
          ))}
        </div>
      )}

      {copyTarget && (
        <CopyToFieldDialog
          note={copyTarget.note}
          field={copyTarget.field}
          existing={caseFields[copyTarget.field] ?? ''}
          onClose={() => setCopyTarget(null)}
          onConfirm={async (mode) => {
            const t = copyTarget;
            setCopyTarget(null);
            await performCopy(t.note.id, t.field, mode);
          }}
        />
      )}
    </div>
  );
}

/**
 * Portal-mounted dropdown menu anchored to a kebab trigger.
 *
 * Why a portal: the trigger lives inside `.full-panel` / `.case-pane`
 * which clip overflow (rounded-card edges). An absolutely-positioned
 * menu inside that subtree gets cut off by ancestor `overflow:hidden`.
 * Rendering to document.body sidesteps every ancestor's clipping.
 *
 * Position is recomputed on open + on scroll/resize. Closes on outside
 * click, scroll past a small threshold, or Escape.
 */
function KebabMenu({
  ariaLabel,
  isOpen,
  onToggle,
  onClose,
  children,
}: {
  ariaLabel: string;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);

  function computePos() {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({
      top: r.bottom + 4,
      right: window.innerWidth - r.right,
    });
  }

  useEffect(() => {
    if (!isOpen) {
      setPos(null);
      return;
    }
    computePos();
    function onDocClick(e: MouseEvent) {
      const tgt = e.target as Node;
      if (triggerRef.current?.contains(tgt)) return;
      if (menuRef.current?.contains(tgt)) return;
      onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    function onScrollOrResize() { onClose(); }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [isOpen, onClose]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="btn-cancel"
        onClick={onToggle}
        style={{ padding: '2px 8px', fontSize: 11 }}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        ⋯
      </button>
      {isOpen && pos
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              style={{
                position: 'fixed',
                top: pos.top,
                right: pos.right,
                minWidth: 240,
                maxWidth: 'min(320px, calc(100vw - 24px))',
                background: 'var(--surface)',
                border: '1px solid var(--border2)',
                borderRadius: 10,
                boxShadow: '0 8px 28px rgba(0,0,0,.18)',
                padding: 6,
                zIndex: 1000,
                fontSize: 12,
              }}
            >
              {children}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function NoteRow({
  note,
  fmtDateTime,
  onTogglePrivacy,
  onDelete,
  onRetry,
  onCopyToField,
  onOpenAttachment,
}: {
  note: CaseNoteSummary;
  fmtDateTime: (iso: string) => string;
  onTogglePrivacy: () => void;
  onDelete: () => void;
  onRetry: () => void;
  onCopyToField: (field: CopyField) => void;
  onOpenAttachment: () => void;
}) {
  const { t } = useTranslation(['cases', 'common']);
  const [showFull, setShowFull] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const isProcessing =
    note.processingStatus !== 'done' && note.processingStatus !== 'failed';
  const failed = note.processingStatus === 'failed';

  return (
    <div
      style={{
        border: '1px solid var(--border2)',
        borderRadius: 10,
        padding: 12,
        background: note.isPrivate ? 'var(--bg2)' : 'transparent',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 12,
          color: 'var(--muted)',
          marginBottom: 6,
          flexWrap: 'wrap',
        }}
      >
        <span>{note.author?.name || note.author?.email || '—'}</span>
        <span>·</span>
        <span>{fmtDateTime(note.createdAt)}</span>
        {note.documentId && (
          <button
            type="button"
            className="btn-cancel"
            onClick={onOpenAttachment}
            style={{ padding: '2px 8px', fontSize: 11 }}
            title={t('detail.notes_open_attachment')}
          >
            📎 {t('detail.notes_view_file')}
          </button>
        )}
        <span
          className="pill"
          onClick={onTogglePrivacy}
          style={{ cursor: 'pointer', marginInlineStart: 'auto' }}
          title={t('detail.notes_toggle_privacy')}
        >
          {note.isPrivate ? '🔒' : '👥'}{' '}
          {t(note.isPrivate ? 'detail.notes_private' : 'detail.notes_firm')}
        </span>
        <KebabMenu
          ariaLabel={t('common:actions.more', { defaultValue: 'More' })}
          isOpen={menuOpen}
          onToggle={() => setMenuOpen((v) => !v)}
          onClose={() => setMenuOpen(false)}
        >
          {/* Retry available on every note with a Document — failed
              rows obviously, but also done rows so the user can
              re-extract / re-summarise after a prompt change. The
              R2 file is preserved so retries never need a re-upload. */}
          {note.documentId && !isProcessing && (
            <button
              type="button"
              className="btn-link"
              style={{ display: 'block', padding: '6px 8px', textAlign: 'start', width: '100%' }}
              onClick={() => { setMenuOpen(false); onRetry(); }}
            >
              ↻ {failed ? t('detail.notes_retry') : t('detail.notes_reprocess')}
            </button>
          )}
          {!isProcessing && (note.summary || note.extractedText || note.body) && (
            <>
              <div style={{ padding: '6px 8px', color: 'var(--text3)', fontSize: 11 }}>
                {t('detail.notes_copy_to')}
              </div>
              {COPY_FIELDS.map((f) => (
                <button
                  key={f}
                  type="button"
                  className="btn-link"
                  style={{ display: 'block', padding: '6px 8px', textAlign: 'start', width: '100%' }}
                  onClick={() => { setMenuOpen(false); onCopyToField(f); }}
                >
                  → {t(`detail.case_field_${f}`)}
                </button>
              ))}
            </>
          )}
          <button
            type="button"
            className="btn-link"
            style={{ display: 'block', padding: '6px 8px', textAlign: 'start', width: '100%', color: 'var(--red)' }}
            onClick={() => { setMenuOpen(false); onDelete(); }}
          >
            {t('common:actions.delete')}
          </button>
        </KebabMenu>
      </div>

      {note.body && (
        <div
          dir="auto"
          style={{ whiteSpace: 'pre-wrap', fontSize: 14, marginBottom: note.summary || isProcessing || failed ? 8 : 0 }}
        >
          {note.body}
        </div>
      )}

      {/* Async status indicator. Visible until status==='done'. */}
      {isProcessing && (
        <div
          style={{
            fontSize: 12,
            color: 'var(--text2)',
            background: 'var(--accent-light)',
            padding: '6px 10px',
            borderRadius: 8,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <span className="cursor" /> {t(`detail.notes_status_${note.processingStatus}`)}
        </div>
      )}
      {failed && (
        <div
          style={{
            fontSize: 12,
            color: 'var(--red)',
            background: 'var(--red-bg)',
            padding: '6px 10px',
            borderRadius: 8,
          }}
        >
          ⚠ {t('detail.notes_status_failed')}
          {note.processingError ? ` — ${note.processingError}` : ''}
          <button
            type="button"
            className="btn-link"
            onClick={onRetry}
            style={{ marginInlineStart: 8, color: 'var(--red)', textDecoration: 'underline' }}
          >
            {t('detail.notes_retry')}
          </button>
        </div>
      )}

      {/* Summary block (always-visible when present). */}
      {note.summary && (
        <div
          style={{
            fontSize: 13,
            color: 'var(--text)',
            background: 'var(--surface2)',
            padding: '8px 10px',
            borderRadius: 8,
            marginTop: 6,
            whiteSpace: 'pre-wrap',
          }}
        >
          <div
            style={{
              fontSize: 10,
              color: 'var(--text3)',
              marginBottom: 3,
              textTransform: 'uppercase',
              letterSpacing: '.06em',
            }}
          >
            {t('detail.notes_summary_label')}
          </div>
          {/* dir="auto" — let the browser detect Hebrew vs Latin from
              the first strong character so the block flows RTL when
              the summary is Hebrew (period on the correct side, line
              wrapping right-to-left). */}
          <div dir="auto">{note.summary}</div>
        </div>
      )}

      {/* Full extracted text — collapsed by default; expand to view.
          Monospace + pre-wrap so Markdown tables (pipe-delimited) keep
          column alignment without needing a real markdown renderer. */}
      {note.extractedText && (
        <div style={{ marginTop: 6 }}>
          <button
            type="button"
            className="btn-link"
            onClick={() => setShowFull((v) => !v)}
            style={{ fontSize: 12 }}
          >
            {showFull ? t('detail.notes_hide_extracted') : t('detail.notes_show_extracted')}
          </button>
          {showFull && (
            <div
              style={{
                background: 'var(--surface2)',
                padding: '10px 12px',
                borderRadius: 8,
                marginTop: 4,
                maxHeight: 360,
                overflow: 'auto',
              }}
            >
              <ExtractedMarkdown text={note.extractedText} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Confirm dialog when copying a note's summary to a Case field that
 * already has content. Shows the EXISTING value (read-only) and the
 * NEW text being added so the user can decide between append (preserve
 * + add) and overwrite (replace) without surprises.
 *
 * Empty target fields skip this dialog — handlePickCopyField fast-paths
 * to append directly.
 */
function CopyToFieldDialog({
  note,
  field,
  existing,
  onClose,
  onConfirm,
}: {
  note: CaseNoteSummary;
  field: CopyField;
  existing: string;
  onClose: () => void;
  onConfirm: (mode: 'append' | 'overwrite') => void | Promise<void>;
}) {
  const { t } = useTranslation(['cases', 'common']);
  const incoming = (note.summary?.trim() || note.extractedText?.trim() || note.body.trim()) ?? '';
  const stamp = new Date().toISOString().slice(0, 10);
  const previewAppend = existing.trim()
    ? `${existing.trim()}\n\n[${stamp}] ${incoming}`
    : `[${stamp}] ${incoming}`;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="form-card modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="form-card-header">
          <div style={{ flex: 1 }}>
            <div className="form-preview-name">
              {t('detail.notes_copy_dialog_title', {
                field: t(`detail.case_field_${field}`),
              })}
            </div>
            <div className="form-preview-type">{t('detail.notes_copy_dialog_subtitle')}</div>
          </div>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            aria-label={t('common:actions.close')}
          >
            ×
          </button>
        </div>

        <div className="form-section">
          <div className="section-label">{t('detail.notes_copy_dialog_existing')}</div>
          <div
            style={{
              fontSize: 13,
              color: 'var(--text2)',
              background: 'var(--surface2)',
              padding: '10px 12px',
              borderRadius: 8,
              whiteSpace: 'pre-wrap',
              maxHeight: 160,
              overflow: 'auto',
              border: '1px solid var(--border)',
            }}
          >
            {existing.trim() || <em style={{ color: 'var(--text3)' }}>{t('detail.notes_copy_dialog_empty')}</em>}
          </div>
        </div>

        <div className="form-section">
          <div className="section-label">{t('detail.notes_copy_dialog_incoming')}</div>
          <div
            style={{
              fontSize: 13,
              color: 'var(--text)',
              background: 'var(--accent-light)',
              padding: '10px 12px',
              borderRadius: 8,
              whiteSpace: 'pre-wrap',
              maxHeight: 160,
              overflow: 'auto',
            }}
          >
            {incoming}
          </div>
        </div>

        <div className="form-section">
          <div className="section-label">{t('detail.notes_copy_dialog_preview_append')}</div>
          <div
            style={{
              fontSize: 12,
              color: 'var(--text2)',
              background: 'var(--surface)',
              padding: '10px 12px',
              borderRadius: 8,
              whiteSpace: 'pre-wrap',
              maxHeight: 200,
              overflow: 'auto',
              border: '1px dashed var(--border2)',
            }}
          >
            {previewAppend}
          </div>
        </div>

        <div className="form-actions" style={{ flexWrap: 'wrap' }}>
          <button type="button" className="btn-cancel" onClick={onClose}>
            {t('common:actions.cancel')}
          </button>
          <button
            type="button"
            className="btn-cancel"
            onClick={() => void onConfirm('overwrite')}
            style={{ color: 'var(--red)', borderColor: 'var(--red)' }}
          >
            ⟳ {t('detail.notes_copy_dialog_overwrite')}
          </button>
          <button
            type="button"
            className="btn-save"
            onClick={() => void onConfirm('append')}
          >
            + {t('detail.notes_copy_dialog_append')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Messages ────────────────────────────────────────────────────────

function MessagesPane({ c, firmId }: { c: CaseFull; firmId: string }) {
  const { t } = useTranslation(['cases']);
  const [showAll, setShowAll] = useState(false);
  if (!c.client) return null;
  return (
    <div className="full-panel">
      <div className="panel-header" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span>{t('detail.section_messages')}</span>
        <div className="filter-row" style={{ marginInlineStart: 'auto' }}>
          <div
            className={`type-btn ${!showAll ? 'active' : ''}`}
            onClick={() => setShowAll(false)}
            style={{ flex: 'unset', padding: '4px 12px', fontSize: 12 }}
          >
            {t('detail.messages_filter_case')}
          </div>
          <div
            className={`type-btn ${showAll ? 'active' : ''}`}
            onClick={() => setShowAll(true)}
            style={{ flex: 'unset', padding: '4px 12px', fontSize: 12 }}
          >
            {t('detail.messages_filter_client')}
          </div>
        </div>
      </div>
      <MessageThread
        clientId={c.client.id}
        clientName={c.client.name}
        clientPhone={c.client.phone ?? null}
        firmId={firmId}
        {...(showAll ? {} : { caseId: c.id })}
        channel={undefined}
      />
    </div>
  );
}

// ─── Overview ────────────────────────────────────────────────────────

function OverviewPane({
  c,
  time,
  invoices,
  expenses,
}: {
  c: CaseFull;
  time: TimeEntrySummary[];
  invoices: InvoiceSummary[];
  expenses: CaseExpenseSummary[];
  onUpdated: () => void;
}) {
  const { t } = useTranslation(['cases', 'common']);
  const totals = useMemo(() => {
    let billableMin = 0;
    let billableAgorot = 0;
    let unbilledAgorot = 0;
    for (const e of time) {
      if (!e.billable) continue;
      billableMin += e.minutes;
      const rate = e.hourlyRateIls ?? c.hourlyRateIls ?? 0;
      const a = Math.round((e.minutes / 60) * rate * 100);
      billableAgorot += a;
      if (!e.invoiceId) unbilledAgorot += a;
    }
    let invoicedAgorot = 0;
    let paidAgorot = 0;
    for (const i of invoices) {
      if (i.status === 'cancelled') continue;
      invoicedAgorot += i.totalAgorot;
      if (i.status === 'paid') paidAgorot += i.totalAgorot;
    }
    const expenseTotal = expenses.reduce((s, e) => s + e.amountAgorot, 0);
    return { billableMin, billableAgorot, unbilledAgorot, invoicedAgorot, paidAgorot, expenseTotal };
  }, [time, invoices, expenses, c.hourlyRateIls]);

  return (
    <div>
      <div className="metrics" style={{ marginBottom: 18 }}>
        <div className="metric">
          <div className="metric-label">{t('detail.metric_total_hours')}</div>
          <div className="metric-val">
            {(Math.round((totals.billableMin / 60) * 10) / 10).toFixed(1)}
          </div>
        </div>
        <div className="metric">
          <div className="metric-label">{t('detail.metric_billable')}</div>
          <div className="metric-val">{formatIls(totals.billableAgorot)}</div>
          {c.hourlyRateIls != null && (
            <div className="metric-sub">
              {t('detail.metric_billable_sub', { rate: c.hourlyRateIls })}
            </div>
          )}
        </div>
        <div className="metric">
          <div className="metric-label">{t('detail.metric_expenses')}</div>
          <div className="metric-val">{formatIls(totals.expenseTotal)}</div>
          <div className="metric-sub">
            {t('detail.metric_expenses_sub', { count: expenses.length })}
          </div>
        </div>
        <div className="metric">
          <div className="metric-label">{t('detail.metric_paid')}</div>
          <div className="metric-val">{formatIls(totals.paidAgorot)}</div>
          {totals.invoicedAgorot - totals.paidAgorot > 0 && (
            <div className="metric-sub down">
              {t('detail.metric_paid_balance', {
                balance: formatIls(totals.invoicedAgorot - totals.paidAgorot),
              })}
            </div>
          )}
        </div>
      </div>

      <div className="case-overview-grid">
        <div className="full-panel">
          <div className="panel-header">{t('detail.section_case_info')}</div>
          <DRow label={t('detail.field_case_number')} value={c.caseNumber} />
          <DRow label={t('detail.field_procedure_type')} value={c.procedureType ?? '—'} />
          <DRow label={t('detail.field_court')} value={c.court ?? '—'} />
          <DRow label={t('detail.field_opened')} value={fmt(c.openedAt)} />
          <DRow label={t('detail.field_due_at')} value={c.dueAt ? fmt(c.dueAt) : '—'} />
          <DRow
            label={t('detail.field_status')}
            valueNode={
              <span className={`pill pill-status-${c.status}`}>
                {t(`statuses.${c.status}`)}
              </span>
            }
          />
          <DRow
            label={t('detail.field_priority')}
            valueNode={
              <span className={`pill pill-prio-${c.priority}`}>
                {t(`priorities.${c.priority}`)}
              </span>
            }
          />
        </div>
        <div className="full-panel">
          <div className="panel-header">{t('detail.section_client_info')}</div>
          <DRow label={t('detail.field_client')} value={c.client?.name ?? '—'} />
          {c.feeType && (
            <DRow label={t('detail.field_fee_type')} value={t(`fee_types.${c.feeType}`)} />
          )}
          {c.hourlyRateIls != null && (
            <DRow
              label={t('detail.field_hourly_rate')}
              value={`₪${c.hourlyRateIls} / ${t('detail.hour_unit')}`}
            />
          )}
        </div>
      </div>

      {c.description && (
        <div className="full-panel" style={{ marginTop: 16 }}>
          <div className="panel-header">{t('detail.section_description')}</div>
          <div
            dir="auto"
            style={{
              padding: '14px 18px',
              fontSize: 13,
              color: 'var(--text2)',
              lineHeight: 1.7,
              whiteSpace: 'pre-wrap',
            }}
          >
            {c.description}
          </div>
        </div>
      )}
    </div>
  );

  function fmt(iso: string) {
    return new Date(iso).toLocaleDateString('he-IL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }
}

function DRow({
  label,
  value,
  valueNode,
}: {
  label: string;
  value?: string;
  valueNode?: React.ReactNode;
}) {
  return (
    <div className="drow">
      <span className="dlabel">{label}</span>
      {/* dir="auto" — flow Hebrew values RTL even on an LTR system
          locale. The browser uses the first strong character to
          decide, so mixed values (case numbers, dates) stay LTR. */}
      <span className="dval" dir="auto">
        {valueNode ?? value}
      </span>
    </div>
  );
}

// ─── Documents ──────────────────────────────────────────────────────

function fileExtFromName(name: string): string {
  const m = /\.([a-z0-9]+)$/i.exec(name);
  return (m?.[1] ?? '').toUpperCase();
}

function fileSizeShort(bytes: number | null | undefined): string {
  if (!bytes || bytes <= 0) return '';
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)}M`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)}K`;
  return `${bytes}B`;
}

function fileIconClass(ext: string): string {
  if (['PDF'].includes(ext)) return 'li-pdf';
  if (['DOC', 'DOCX'].includes(ext)) return 'li-doc';
  if (['JPG', 'JPEG', 'PNG', 'GIF', 'WEBP', 'HEIC'].includes(ext)) return 'li-jpg';
  return 'li-other';
}

function docStatusPillClass(status: string): string {
  switch (status) {
    case 'signed':
      return 'sp-paid';
    case 'pending_signature':
      return 'sp-over';
    case 'template':
      return 'sp-blue';
    case 'archived':
      return 'sp-pend';
    case 'draft':
    default:
      return 'sp-draft';
  }
}

function DocumentsPane({
  docs,
  onUpload,
  onSend,
  onSendAll,
  onOpenDoc,
  fmtDate,
}: {
  docs: DocumentSummary[];
  onUpload: () => void;
  onSend: (doc: DocumentSummary, channel?: 'wa' | 'email') => void;
  onSendAll: () => void;
  onOpenDoc: () => void;
  fmtDate: (iso: string | null) => string;
}) {
  const { t } = useTranslation(['cases', 'common', 'documents']);
  return (
    <div>
      <div className="full-panel">
        <div className="panel-header">
          <span>
            {t('detail.section_documents')} ({docs.length})
          </span>
          <span style={{ display: 'flex', gap: 6 }}>
            {docs.length > 1 && (
              <button className="btn-link" onClick={onSendAll}>
                {t('detail.action_send_all')}
              </button>
            )}
            <button className="btn-link" onClick={onUpload}>
              + {t('detail.action_upload')}
            </button>
          </span>
        </div>
        {docs.length === 0 ? (
          <div className="empty">{t('detail.empty_documents')}</div>
        ) : (
          docs.map((d) => {
            const ext = fileExtFromName(d.name) || 'FILE';
            const size = fileSizeShort(d.latestVersion?.size);
            return (
              <div key={d.id} className="case-row" style={{ cursor: 'pointer' }}>
                <div className={`lrow-icon ${fileIconClass(ext)}`} aria-hidden="true">
                  <span>{ext.slice(0, 4)}</span>
                  {size && <span style={{ opacity: 0.85 }}>{size}</span>}
                </div>
                <div className="case-info" onClick={onOpenDoc}>
                  <div className="case-name" dir="auto">{d.name}</div>
                  <div className="case-sub">
                    v{d.currentVersion} · {fmtDate(d.updatedAt)}
                  </div>
                </div>
                <div className="case-row-actions">
                  <button
                    className="btn-wa"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSend(d, 'wa');
                    }}
                  >
                    {t('detail.action_send_wa')}
                  </button>
                  <button
                    className="btn-mail"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSend(d, 'email');
                    }}
                  >
                    {t('detail.action_send_mail')}
                  </button>
                  <span className={`sp ${docStatusPillClass(d.status)}`}>
                    {t(`documents:statuses.${d.status}`, { defaultValue: d.status })}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ─── Contacts ───────────────────────────────────────────────────────

function ContactsPane({
  c,
  contacts,
  onAdd,
  onEdit,
}: {
  c: CaseFull;
  contacts: CaseContactSummary[];
  onAdd: () => void;
  onEdit: (contact: CaseContactSummary) => void;
}) {
  const { t } = useTranslation(['cases', 'common']);
  return (
    <div>
      <div className="full-panel">
        <div className="panel-header">
          <span>
            {t('detail.section_contacts')} ({contacts.length + 1})
          </span>
          <button className="btn-link" onClick={onAdd}>
            + {t('detail.action_add_contact')}
          </button>
        </div>

        {/* Primary client (always shown, not editable here — edit via clients page) */}
        {c.client && (
          <div className="case-row">
            <Avatar name={c.client.name} size="sm" />
            <div className="case-info">
              <div className="case-name">{c.client.name}</div>
              <div className="case-sub">{t('detail.contact_primary_client')}</div>
            </div>
            <span className="pill pill-area">{t('contact_kinds.primary_client')}</span>
          </div>
        )}

        {contacts.length === 0 ? (
          <div className="empty">{t('detail.empty_contacts')}</div>
        ) : (
          contacts.map((contact) => (
            <div
              key={contact.id}
              className="case-row"
              style={{ cursor: 'pointer' }}
              onClick={() => onEdit(contact)}
            >
              <Avatar name={contact.name} size="sm" />
              <div className="case-info">
                <div className="case-name">{contact.name}</div>
                <div className="case-sub">
                  {[contact.phone, contact.email].filter(Boolean).join(' · ') || '—'}
                </div>
              </div>
              <span className="pill pill-area">{t(`contact_kinds.${contact.kind}`)}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ─── Tasks ──────────────────────────────────────────────────────────

function TasksPane({
  tasks,
  onAdd,
  onEdit,
  onToggle,
}: {
  tasks: TaskSummary[];
  onAdd: () => void;
  onEdit: (task: TaskSummary) => void;
  onToggle: (task: TaskSummary) => void;
}) {
  const { t, i18n } = useTranslation(['cases', 'common', 'tasks']);
  const open = tasks.filter((tt) => tt.status === 'open');
  const done = tasks.filter((tt) => tt.status === 'done');
  const high = open.filter((tt) => tt.priority === 'high');
  // "This week" = open tasks with a dueAt within the next 7 days
  const now = Date.now();
  const weekHorizon = now + 7 * 24 * 60 * 60 * 1000;
  const thisWeek = open.filter(
    (tt) => tt.dueAt && new Date(tt.dueAt).getTime() <= weekHorizon,
  );
  const completionPct = tasks.length > 0 ? Math.round((done.length / tasks.length) * 100) : 0;

  return (
    <div>
      <div className="metrics" style={{ marginBottom: 18 }}>
        <div className="metric">
          <div className="metric-label">{t('detail.tasks_metric_total')}</div>
          <div className="metric-val">{tasks.length}</div>
        </div>
        <div className="metric">
          <div className="metric-label">{t('detail.tasks_metric_done')}</div>
          <div className="metric-val">{done.length}</div>
          {tasks.length > 0 && (
            <div className="metric-sub up">{completionPct}%</div>
          )}
        </div>
        <div className="metric">
          <div className="metric-label">{t('detail.tasks_metric_high')}</div>
          <div className="metric-val">{high.length}</div>
        </div>
        <div className="metric">
          <div className="metric-label">{t('detail.tasks_metric_week')}</div>
          <div className="metric-val">{thisWeek.length}</div>
        </div>
      </div>

      <div className="full-panel">
        <div className="panel-header">
          <span>{t('detail.section_tasks')}</span>
          <button className="btn-link" onClick={onAdd}>
            + {t('detail.action_add_task')}
          </button>
        </div>
        {tasks.length === 0 ? (
          <div className="empty">{t('detail.empty_tasks')}</div>
        ) : (
          tasks.map((task) => (
            <div
              key={task.id}
              className="task-row"
              style={{ cursor: 'pointer' }}
              onClick={() => onEdit(task)}
            >
              <div
                className={`task-check ${task.status === 'done' ? 'done' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggle(task);
                }}
              />
              <div className="task-info">
                <div className={`task-text ${task.status === 'done' ? 'done' : ''}`}>
                  {task.title}
                </div>
                <div className="task-meta">
                  {task.dueAt && (
                    <span className="task-date">
                      {new Date(task.dueAt).toLocaleDateString(i18n.language, {
                        day: '2-digit',
                        month: '2-digit',
                      })}
                    </span>
                  )}
                  <span className={`task-priority prio-${task.priority}`}>
                    {t(`tasks:priorities.${task.priority}`)}
                  </span>
                  {task.assignee && (
                    <span className="task-assign">
                      · {task.assignee.name ?? task.assignee.email}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ─── Time ───────────────────────────────────────────────────────────

function TimePane({
  c,
  time,
  invoices,
  onSaved,
  fmtDate,
}: {
  c: CaseFull;
  time: TimeEntrySummary[];
  invoices: InvoiceSummary[];
  onSaved: () => void;
  fmtDate: (iso: string | null) => string;
}) {
  const { t } = useTranslation(['cases', 'common']);
  const totalMin = time.reduce((s, e) => s + e.minutes, 0);
  const totalAgorot = time.reduce((s, e) => {
    const rate = e.hourlyRateIls ?? c.hourlyRateIls ?? 0;
    return s + Math.round((e.minutes / 60) * rate * 100);
  }, 0);
  // Billed = sum of paid + issued invoices; outstanding = issued only.
  const paidAgorot = invoices.reduce(
    (s, i) => (i.status === 'paid' ? s + i.totalAgorot : s),
    0,
  );
  const issuedAgorot = invoices.reduce(
    (s, i) => (i.status === 'issued' ? s + i.totalAgorot : s),
    0,
  );

  return (
    <div>
      <div className="metrics" style={{ marginBottom: 18 }}>
        <div className="metric">
          <div className="metric-label">{t('detail.metric_total_hours')}</div>
          <div className="metric-val">
            {(Math.round((totalMin / 60) * 10) / 10).toFixed(1)}
          </div>
        </div>
        <div className="metric">
          <div className="metric-label">{t('detail.metric_billable')}</div>
          <div className="metric-val">{formatIls(totalAgorot)}</div>
        </div>
        <div className="metric">
          <div className="metric-label">{t('detail.metric_paid')}</div>
          <div className="metric-val">{formatIls(paidAgorot)}</div>
          {paidAgorot > 0 && (
            <div className="metric-sub up">{t('expense_statuses.paid')}</div>
          )}
        </div>
        <div className="metric">
          <div className="metric-label">{t('detail.metric_outstanding')}</div>
          <div className="metric-val">{formatIls(issuedAgorot)}</div>
        </div>
      </div>

      <div style={{ marginBottom: 18 }}>
        <TimeEntryWidget
          caseId={c.id}
          caseLabel={c.caseNumber + (c.area ? ` · ${c.area}` : '')}
          onSaved={onSaved}
        />
      </div>

      <div className="full-panel">
        <div className="panel-header">{t('detail.section_time')}</div>
        {time.length === 0 ? (
          <div className="empty">{t('detail.empty_time')}</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="htable">
              <thead>
                <tr>
                  <th>{t('detail.col_description')}</th>
                  <th>{t('detail.col_date')}</th>
                  <th>{t('detail.col_hours')}</th>
                  <th>{t('detail.col_amount')}</th>
                </tr>
              </thead>
              <tbody>
                {time.map((e) => {
                  const rate = e.hourlyRateIls ?? c.hourlyRateIls ?? 0;
                  const lineAgorot = Math.round((e.minutes / 60) * rate * 100);
                  const hours = Math.round((e.minutes / 60) * 10) / 10;
                  return (
                    <tr key={e.id}>
                      <td>{e.description ?? '—'}</td>
                      <td>{fmtDate(e.startedAt)}</td>
                      <td>{hours.toFixed(1)}</td>
                      <td style={{ fontWeight: 500 }}>
                        {e.billable ? formatIlsExact(lineAgorot) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td style={{ fontWeight: 500 }}>{t('detail.total_label')}</td>
                  <td></td>
                  <td style={{ fontWeight: 500 }}>
                    {(Math.round((totalMin / 60) * 10) / 10).toFixed(1)}
                  </td>
                  <td style={{ fontWeight: 500 }}>{formatIlsExact(totalAgorot)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Expenses ───────────────────────────────────────────────────────

function ExpensesPane({
  expenses,
  summary,
  onAdd,
  onEdit,
  fmtDate,
}: {
  expenses: CaseExpenseSummary[];
  summary: ExpensesListResp['summary'];
  onAdd: () => void;
  onEdit: (expense: CaseExpenseSummary) => void;
  fmtDate: (iso: string | null) => string;
}) {
  const { t } = useTranslation(['cases', 'common']);
  return (
    <div>
      <div className="metrics" style={{ marginBottom: 18 }}>
        <div className="metric">
          <div className="metric-label">{t('detail.section_expenses_total')}</div>
          <div className="metric-val">{formatIls(summary.totalAgorot)}</div>
        </div>
        <div className="metric">
          <div className="metric-label">{t('expense_statuses.paid')}</div>
          <div className="metric-val">{formatIls(summary.paidAgorot)}</div>
        </div>
        <div className="metric">
          <div className="metric-label">{t('expense_statuses.pending')}</div>
          <div className="metric-val">{formatIls(summary.pendingAgorot)}</div>
        </div>
        <div className="metric">
          <div className="metric-label">{t('detail.expenses_count')}</div>
          <div className="metric-val">{summary.count}</div>
        </div>
      </div>

      <div className="full-panel">
        <div className="panel-header">
          <span>{t('detail.section_expenses')}</span>
          <button className="btn-link" onClick={onAdd}>
            + {t('detail.action_add_expense')}
          </button>
        </div>
        {expenses.length === 0 ? (
          <div className="empty">{t('detail.empty_expenses')}</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="htable">
              <thead>
                <tr>
                  <th>{t('detail.col_description')}</th>
                  <th>{t('detail.col_date')}</th>
                  <th>{t('detail.col_amount')}</th>
                  <th>{t('detail.col_status')}</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((e) => (
                  <tr key={e.id} style={{ cursor: 'pointer' }} onClick={() => onEdit(e)}>
                    <td>{e.description}</td>
                    <td>{fmtDate(e.incurredAt)}</td>
                    <td style={{ fontWeight: 500 }}>{formatIlsExact(e.amountAgorot)}</td>
                    <td>
                      <span
                        className={`pill ${
                          e.status === 'paid' ? 'pill-status-open' : 'pill-prio-medium'
                        }`}
                      >
                        {t(`expense_statuses.${e.status}`)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td style={{ fontWeight: 500 }}>{t('detail.total_label')}</td>
                  <td></td>
                  <td style={{ fontWeight: 500 }}>{formatIlsExact(summary.totalAgorot)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Timeline ───────────────────────────────────────────────────────

function TimelinePane({
  timeline,
  onAdd,
  onEdit,
  fmtDate,
}: {
  timeline: CaseTimelineEventSummary[];
  onAdd: () => void;
  onEdit: (event: CaseTimelineEventSummary) => void;
  fmtDate: (iso: string | null) => string;
}) {
  const { t } = useTranslation(['cases', 'common']);
  return (
    <div className="full-panel">
      <div className="panel-header">
        <span>{t('detail.section_timeline')}</span>
        <button className="btn-link" onClick={onAdd}>
          + {t('detail.action_add_timeline')}
        </button>
      </div>
      {timeline.length === 0 ? (
        <div className="empty">{t('detail.empty_timeline')}</div>
      ) : (
        <div className="case-timeline">
          {timeline.map((ev, i) => (
            <div
              key={ev.id}
              className="tl-item"
              style={{ cursor: 'pointer' }}
              onClick={() => onEdit(ev)}
            >
              <div
                className="tl-dot"
                style={{ background: ev.color ?? 'var(--accent)' }}
              />
              {i < timeline.length - 1 && <div className="tl-line" />}
              <div className="tl-body">
                <div className="tl-head">
                  <span className="tl-title">{ev.title}</span>
                  <span className="tl-date">{fmtDate(ev.eventAt)}</span>
                </div>
                {ev.description && <div className="tl-desc">{ev.description}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Invoices ───────────────────────────────────────────────────────

function invoiceStatusPillClass(status: InvoiceStatus): string {
  switch (status) {
    case 'paid':
      return 'sp-paid';
    case 'issued':
      return 'sp-over';
    case 'draft':
      return 'sp-draft';
    case 'cancelled':
      return 'sp-pend';
  }
}

function InvoicesPane({
  invoices,
  onAdd,
  onSendReminder,
  fmtDate,
}: {
  invoices: InvoiceSummary[];
  onAdd: () => void;
  onSendReminder: (invoice: InvoiceSummary) => void;
  fmtDate: (iso: string | null) => string;
}) {
  const { t } = useTranslation(['cases', 'common', 'billing']);
  const totals = useMemo(() => {
    let total = 0;
    let paid = 0;
    for (const i of invoices) {
      if (i.status === 'cancelled') continue;
      total += i.totalAgorot;
      if (i.status === 'paid') paid += i.totalAgorot;
    }
    return { total, paid, outstanding: total - paid };
  }, [invoices]);

  return (
    <div>
      <div className="metrics" style={{ marginBottom: 18 }}>
        <div className="metric">
          <div className="metric-label">{t('detail.invoices_count')}</div>
          <div className="metric-val">{invoices.length}</div>
        </div>
        <div className="metric">
          <div className="metric-label">{t('detail.metric_paid')}</div>
          <div className="metric-val">{formatIls(totals.paid)}</div>
          {totals.paid > 0 && (
            <div className="metric-sub up">{t('expense_statuses.paid')}</div>
          )}
        </div>
        <div className="metric">
          <div className="metric-label">{t('detail.metric_outstanding')}</div>
          <div className="metric-val">{formatIls(totals.outstanding)}</div>
          {totals.outstanding > 0 && (
            <div className="metric-sub down">{t('detail.metric_outstanding')}</div>
          )}
        </div>
        <div className="metric">
          <div className="metric-label">{t('detail.metric_invoiced')}</div>
          <div className="metric-val">{formatIls(totals.total)}</div>
        </div>
      </div>

      <div className="full-panel">
        <div className="panel-header">
          <span>{t('detail.section_invoices')}</span>
          <button className="btn-link" onClick={onAdd}>
            + {t('detail.action_add_invoice')}
          </button>
        </div>
        {invoices.length === 0 ? (
          <div className="empty">{t('detail.empty_invoices')}</div>
        ) : (
          <>
            {invoices.map((i) => {
              const isOutstanding = i.status === 'issued';
              return (
                <div key={i.id} className="case-row">
                  <div className="case-info">
                    <div className="case-name" dir="auto">
                      #{i.greeninvoiceNumber ?? i.id.slice(0, 8)}
                      {i.description ? ` · ${i.description}` : ''}
                    </div>
                    <div className="case-sub">{fmtDate(i.issuedAt ?? i.createdAt)}</div>
                  </div>
                  <div className="case-row-actions">
                    <span
                      style={{
                        fontWeight: 500,
                        color: isOutstanding ? '#8B1A1A' : 'var(--text)',
                      }}
                    >
                      {formatIlsExact(i.totalAgorot)}
                    </span>
                    <span className={`sp ${invoiceStatusPillClass(i.status)}`}>
                      {t(`billing:status.${i.status}`, { defaultValue: i.status })}
                    </span>
                    {i.documentUrl && (
                      <a
                        className="btn-cancel"
                        href={i.documentUrl}
                        target="_blank"
                        rel="noopener"
                        style={{ padding: '5px 10px', fontSize: 12, textDecoration: 'none' }}
                      >
                        PDF
                      </a>
                    )}
                    {isOutstanding && (
                      <button
                        type="button"
                        className="btn-primary"
                        style={{ padding: '5px 10px', fontSize: 12 }}
                        onClick={() => onSendReminder(i)}
                      >
                        {t('detail.action_send_reminder')}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            <div
              style={{
                padding: '11px 18px',
                background: 'var(--bg, #F2F0EB)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: 12,
                fontWeight: 500,
                borderTop: '0.5px solid var(--rule2)',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              <span>{t('detail.total_label')}</span>
              <span>
                {t('detail.summary_paid_outstanding', {
                  total: formatIlsExact(totals.total).replace('₪', ''),
                  paid: formatIlsExact(totals.paid).replace('₪', ''),
                  outstanding: formatIlsExact(totals.outstanding).replace('₪', ''),
                })}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
